import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MuiTooltip from "@mui/material/Tooltip";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Title,
  Tooltip as ChartTooltip,
} from "chart.js";
import { Bar } from "react-chartjs-2";

import emissionViewStore from "../../Store/marco/emissionViewStore";
import commonStore from "../../Store/marco/commonStore";
import { boundarySettingsAPI } from "../../BoundarySettings/API/boundarySettingsAPI";
import { emissionActivityModalAPI } from "../API/emissionActivityAPI";
import handleAPIError, {
  MESSAGE_CODES,
  showSuccessToast,
} from "../../services/toast";
import styles from "../styles/emissionRemovalMitigation.module.css";
import {
  getSelectComOptionByCompanyList,
  getSelectComOptionByOrgList,
} from "../utils/getSelectComOption";
import {
  RM_ACTIVITY_TYPES,
  RM_ACTIVITY_TYPE_OTHERS,
  RM_SCOPE_OPTIONS,
  calculateRMActivity,
  createRMActivity,
  formatRMActivityType,
  formatRMNumber,
  getRMScenario,
  getRMScenarioMeta,
  getRMSummary,
} from "../utils/emissionRemovalMitigation";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  LineController,
  ChartTooltip,
  Legend,
  Title,
);

const DEFAULT_FORM = {
  entity: "",
  companyId: "",
  activityName: "",
  rmType: "",
  rmSpecify: "",
  q1: "no",
  q2: "no",
  beforeScope: "",
  beforeCategory: "",
  beforeActivity: "",
  beforeSource: "",
  afterScope: "",
  afterCategory: "",
  afterActivity: "",
  afterSource: "",
  replacementPct: 50,
};

const BEFORE_ACTIVITY_REQUIRED_MESSAGE =
  "Before must contain emission activity data before creating an R&M activity.";
const DIFFERENT_SCOPE_REQUIRED_MESSAGE =
  "Before and After Scope must be different.";
const NO_DIFFERENT_AFTER_SCOPE_MESSAGE =
  "No different scope with emission activity data is available for the reporting year.";
const SELECT_ENTITY_FIRST_MESSAGE = "Select a Reporting Entity first";
const SAME_AS_BEFORE_MESSAGE = "Same as Before";
const NO_MATCHING_EF_MESSAGE =
  "No activity with the same emission factor is available in the reporting year.";
const RM_SUMMARY_PAGE_SIZE = 10;
const RM_GWP_FIELDS = [
  ["CO2", "co2GWP"],
  ["CH4", "ch4GWP"],
  ["N2O", "n2oGWP"],
  ["HFCs", "hfcsGWP"],
  ["PFCs", "pfcsGWP"],
  ["SF6", "sf6GWP"],
  ["NF3", "nf3GWP"],
];
const GAS_DISPLAY_NAMES = {
  CO2: "CO₂",
  CH4: "CH₄",
  N2O: "N₂O",
  HFCs: "HFCs",
  PFCs: "PFCs",
  SF6: "SF₆",
  NF3: "NF₃",
};

const RM_STANDARD_STATUS = {
  CHECKING: "checking",
  READY: "ready",
  MISMATCH: "mismatch",
  UNAVAILABLE: "unavailable",
};

const getStandardLabel = (standardId) => {
  const labels = {
    1: "ISO 14064-1",
    2: "GHG Protocol",
  };

  return labels[standardId] || `Standard ${standardId}`;
};

const fetchRMStandardContext = async ({ rootId, userId, reportingYear }) => {
  const response = await boundarySettingsAPI.getBSContent({
    rootId,
    userId,
    reportId: 1,
    year: reportingYear,
    trigger: false,
  });
  const boundaryContent = response?.data;
  const baseYear = toFiniteNumberOrNull(boundaryContent?.baseYear);
  const reportingStandardId = toFiniteNumberOrNull(
    boundaryContent?.standardId,
  );

  if (baseYear === null || reportingStandardId === null) {
    return {
      status: RM_STANDARD_STATUS.UNAVAILABLE,
      baseYear,
      reportingYear,
    };
  }

  const baseStandardId = toFiniteNumberOrNull(
    await boundarySettingsAPI.getStandardIdByYear(userId, baseYear),
  );

  if (baseStandardId === null) {
    return {
      status: RM_STANDARD_STATUS.UNAVAILABLE,
      baseYear,
      reportingYear,
      reportingStandardId,
    };
  }

  return {
    status:
      baseStandardId === reportingStandardId
        ? RM_STANDARD_STATUS.READY
        : RM_STANDARD_STATUS.MISMATCH,
    baseYear,
    reportingYear,
    baseStandardId,
    reportingStandardId,
  };
};

const isRMEmptyTableValue = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === "number") return value === 0;
  if (typeof value !== "string") return false;

  const normalized = value.trim();
  if (!normalized) return true;

  const numericText = normalized.endsWith("%")
    ? normalized.slice(0, -1).trim()
    : normalized;
  const number = Number(numericText);
  return Number.isFinite(number) && number === 0;
};

const formatRMTableText = (value) => (isRMEmptyTableValue(value) ? "-" : value);

const getScopeFromCategory = (category) => {
  if (!category) return "";
  return String(category).split(/[_.]/)[0] || "";
};

const getInitialRMForm = (activity) => {
  if (!activity?.editInfo) return { ...DEFAULT_FORM };

  const editInfo = activity.editInfo;
  const initialForm = {
    ...DEFAULT_FORM,
    entity: activity.companyName || "",
    companyId: editInfo.companyId || "",
    activityName: editInfo.itemName || "",
    rmType:
      editInfo.activityNameId === null || editInfo.activityNameId === undefined
        ? ""
        : String(editInfo.activityNameId),
    rmSpecify: editInfo.otherName || "",
    q1: editInfo.isCrossCategory ? "yes" : "no",
    q2: editInfo.isSourceChanged ? "yes" : "no",
    beforeScope: getScopeFromCategory(editInfo.beforeCategory),
    beforeCategory: editInfo.beforeCategory || "",
    beforeActivity: editInfo.beforeActivityId || "",
    afterScope: getScopeFromCategory(editInfo.afterCategory),
    afterCategory: editInfo.afterCategory || "",
    afterActivity: editInfo.afterActivityId || "",
    replacementPct:
      editInfo.replacement === null || editInfo.replacement === undefined
        ? DEFAULT_FORM.replacementPct
        : editInfo.replacement,
  };

  if (
    initialForm.q1 === "yes" &&
    initialForm.beforeScope &&
    initialForm.beforeScope === initialForm.afterScope
  ) {
    return {
      ...initialForm,
      afterScope: "",
      afterCategory: "",
      afterActivity: "",
    };
  }

  return initialForm;
};

const toCategoryMap = (dropdown) =>
  Object.entries(dropdown || {}).reduce((map, [scope, categories]) => {
    map[scope] = (categories || []).map((category) => category.code);
    return map;
  }, {});

const hasDropdownActivity = (dropdown) =>
  Object.values(dropdown || {}).some(
    (categories) => Array.isArray(categories) && categories.length > 0,
  );

const toFiniteNumberOrNull = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const getGasEmissionFactor = (gases = []) => {
  const values = gases
    .map((item) => toFiniteNumberOrNull(item?.value))
    .filter((value) => value !== null);

  if (!values.length) return null;
  return values.reduce((sum, value) => sum + value, 0);
};

const normalizeRemovalEmissionActivity = (item = {}) => {
  const data = toFiniteNumberOrNull(item.rawData);
  const ef = getGasEmissionFactor(item.gases);
  const efId = item.EFId ?? item.efId ?? null;
  const hasRawData =
    typeof item.hasRawdata === "boolean" ? item.hasRawdata : data !== null;
  const hasEF =
    typeof item.hasEFId === "boolean"
      ? item.hasEFId
      : efId !== null && efId !== undefined && Number(efId) !== 0;

  return {
    id: item.activityId,
    activityId: item.activityId,
    EFId: efId,
    name: item.itemName || "-",
    data,
    ef,
    unit: item.unit || "",
    efUnit: item.efUnit || "",
    efName: item.efName || "",
    emissionSourceName: item.emissionSourceName || "",
    hasRawData,
    hasEF,
    gwpValues: RM_GWP_FIELDS.map(([gas, key]) => ({
      gas,
      value: item[key],
    })).filter(({ value }) => value !== null && value !== undefined),
    gases: item.gases || [],
    raw: item,
  };
};

const getGasDisplayName = (gas) => GAS_DISPLAY_NAMES[gas] || gas || "-";

const formatActivityDataValue = (activity, t, includePeriod = true) => {
  if (!activity?.hasRawData || activity.data === null) {
    return t("Not entered");
  }

  const unit = activity.unit || t("Not assigned");
  const period = includePeriod ? "/yr" : "";
  return `${Number(activity.data).toLocaleString()} ${unit}${period}`;
};

const getEmissionFactorValues = (activity) =>
  (activity?.gases || [])
    .map((item) => ({
      gas: item?.gas,
      value: item?.value,
      numericValue: toFiniteNumberOrNull(item?.value),
    }))
    .filter(
      (item) =>
        item.gas && item.numericValue !== null && item.numericValue !== 0,
    );

const getEmissionSourceLabel = (activity, t) => {
  if (!activity?.hasEF) return t("Emission factor not assigned");

  const parts = [activity.emissionSourceName, activity.efName].filter(Boolean);
  if (!parts.length) return t("Emission factor not assigned");

  const label = parts.join(" · ");
  return activity.efUnit ? `${label} - ${activity.efUnit}` : label;
};

const normalizeForm = (input, options = {}) => {
  const next = { ...input };
  const beforeCategoryMap = options.beforeCategoryMap || {};
  const afterCategoryMap = options.afterCategoryMap || {};

  if (next.q1 === "yes") next.q2 = "yes";

  const scenario = getRMScenario(next);
  if (next.beforeScope && !beforeCategoryMap[next.beforeScope]) {
    next.beforeScope = "";
    next.beforeCategory = "";
    next.beforeActivity = "";
  } else if (!next.beforeScope) {
    next.beforeCategory = "";
    next.beforeActivity = "";
  } else {
    const beforeCategories = beforeCategoryMap[next.beforeScope] || [];
    if (
      next.beforeCategory &&
      !beforeCategories.includes(next.beforeCategory)
    ) {
      next.beforeCategory = "";
      next.beforeActivity = "";
    }
  }

  if (scenario !== "S1") {
    next.afterScope = next.beforeScope;
    next.afterCategory = next.beforeCategory;
  } else {
    if (
      !next.beforeScope ||
      (next.afterScope && String(next.afterScope) === String(next.beforeScope))
    ) {
      next.afterScope = "";
      next.afterCategory = "";
      next.afterActivity = "";
    } else if (next.afterScope && !afterCategoryMap[next.afterScope]) {
      next.afterScope = "";
      next.afterCategory = "";
      next.afterActivity = "";
    } else if (!next.afterScope) {
      next.afterCategory = "";
      next.afterActivity = "";
    } else {
      const afterCategories = afterCategoryMap[next.afterScope] || [];
      if (next.afterCategory && !afterCategories.includes(next.afterCategory)) {
        next.afterCategory = "";
        next.afterActivity = "";
      }
    }
  }

  if (scenario === "S3") {
    next.afterSource = next.beforeSource;
  }

  return next;
};

const ScenarioChip = ({ scenario }) => {
  if (isRMEmptyTableValue(scenario)) return "-";

  const meta = getRMScenarioMeta(scenario);
  return (
    <span className={`${styles.scenarioChip} ${styles[meta.className]}`}>
      {meta.shortLabel}
    </span>
  );
};

const Field = ({ label, required, error, children }) => (
  <div className={styles.field}>
    <label>
      {label} {required && <span className={styles.required}>*</span>}
      {error && <span className={styles.fieldError}>{error}</span>}
    </label>
    {children}
  </div>
);

const SimpleSelect = ({
  value,
  options,
  onChange,
  disabled = false,
  className = "",
  placeholder,
  displayValue,
  emptyMessage = "No data",
}) => {
  const { t } = useTranslation("ActivityMain");
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const selectRef = useRef(null);
  const normalizedOptions = (options || []).map((option) =>
    typeof option === "string" ? { value: option, label: option } : option,
  );
  const selected = normalizedOptions.find(
    (option) => String(option.value) === String(value),
  );

  const handleBlur = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setOpen(false);
    }
  };

  useLayoutEffect(() => {
    if (!open || !selectRef.current) return;

    const rect = selectRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    setOpenUp(spaceBelow < 260 && spaceAbove > spaceBelow);
  }, [open, normalizedOptions.length]);

  return (
    <div className={styles.activitySelect} onBlur={handleBlur} ref={selectRef}>
      <div className={styles.activitySelectControl}>
        <button
          type="button"
          className={`${styles.activitySelectButton} ${styles.simpleSelectButton} ${className}`}
          aria-haspopup="listbox"
          aria-expanded={open}
          disabled={disabled}
          onClick={() => {
            if (!disabled) {
              setOpen((prev) => !prev);
            }
          }}
        >
          <span className={styles.simpleSelectValue}>
            {displayValue || selected?.label || placeholder || t("Select")}
          </span>
          <span className={styles.selectChevron} aria-hidden="true" />
        </button>
      </div>

      {open && !disabled && (
        <div
          className={`${styles.activitySelectMenu} ${
            openUp ? styles.activitySelectMenuOpenUp : ""
          }`}
          role="listbox"
        >
          {normalizedOptions.length ? (
            normalizedOptions.map((option) => {
              const active = String(option.value) === String(selected?.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={active}
                  className={`${styles.activitySelectOption} ${styles.simpleSelectOption} ${
                    active ? styles.simpleSelectOptionActive : ""
                  }`}
                  onClick={() => {
                    onChange(option.value, option);
                    setOpen(false);
                  }}
                >
                  {option.label}
                </button>
              );
            })
          ) : (
            <div className={styles.activitySelectEmpty}>{t(emptyMessage)}</div>
          )}
        </div>
      )}
    </div>
  );
};

const ActivityStatusTags = ({ activity, t }) => {
  if (!activity) return null;

  return (
    <span className={styles.activityStatusTags}>
      {!activity.hasRawData && (
        <span className={`${styles.activityStatusTag} ${styles.missingDataTag}`}>
          {t("No activity data")}
        </span>
      )}
      {!activity.hasEF && (
        <span className={`${styles.activityStatusTag} ${styles.missingEFTag}`}>
          {t("No emission factor")}
        </span>
      )}
    </span>
  );
};

const EmissionFactorValueList = ({ activity }) => {
  const { t } = useTranslation("ActivityMain");
  const factors = getEmissionFactorValues(activity);

  if (!activity?.hasEF || !factors.length) {
    return <span className={styles.readoutEmpty}>{t("Not assigned")}</span>;
  }

  return (
    <div className={styles.factorValueList}>
      {factors.map(({ gas, value }, index) => (
        <div className={styles.factorValueRow} key={`${gas}-${index}`}>
          <span className={styles.factorIndex}>#{index + 1}</span>
          <strong>{String(value)}</strong>
          <span className={styles.factorUnit}>
            {activity.efUnit
              ? `kg${getGasDisplayName(gas)} / ${activity.efUnit}`
              : t("Not assigned")}
          </span>
        </div>
      ))}
    </div>
  );
};

const ActivityTooltipContent = ({ activity, year }) => {
  const { t } = useTranslation("ActivityMain");

  return (
    <div className={styles.activityTooltipContent}>
      <div className={styles.tooltipDetailRow}>
        <span>{t("Activity Name")}</span>
        <strong>{activity?.name || "-"}</strong>
      </div>
      <div className={styles.tooltipDetailRow}>
        <span>{t("Year")}</span>
        <strong>{Number(year) || "-"}</strong>
      </div>
      <div className={styles.tooltipDetailRow}>
        <span>{t("Activity Data")}</span>
        <strong>{formatActivityDataValue(activity, t, false)}</strong>
      </div>
      <div className={styles.tooltipDetailRow}>
        <span>{t("Emission Factor Name")}</span>
        <strong>
          {activity?.hasEF && activity.efName
            ? activity.efName
            : t("Not assigned")}
        </strong>
      </div>
      <div className={styles.tooltipFactorSection}>
        <span>{t("Emission Factor Value")}</span>
        <EmissionFactorValueList activity={activity} />
      </div>
    </div>
  );
};

const ActivityReadout = ({ activity }) => {
  const { t } = useTranslation("ActivityMain");
  const gwpValues = activity?.gwpValues || [];
  const hasEmissionFactorValues =
    Boolean(activity?.hasEF) && getEmissionFactorValues(activity).length > 0;

  return (
    <div className={styles.activityReadout}>
      <div className={styles.readoutRow}>
        <span>{t("Activity Data")}</span>
        <strong>{formatActivityDataValue(activity, t)}</strong>
      </div>
      <div className={styles.readoutRow}>
        <span>{t("Emission Factor Name")}</span>
        <strong>
          {activity?.hasEF && activity.efName
            ? activity.efName
            : t("Not assigned")}
        </strong>
      </div>
      <div
        className={`${styles.readoutRow} ${
          hasEmissionFactorValues ? styles.readoutStackedRow : ""
        }`}
      >
        <span>{t("Emission Factor Value")}</span>
        <EmissionFactorValueList activity={activity} />
      </div>
      {gwpValues.length > 0 && (
        <div className={`${styles.readoutRow} ${styles.readoutStackedRow}`}>
          <span>{t("GWP")}</span>
          <div className={styles.gwpValueList}>
            {gwpValues.map(({ gas, value }) => (
              <div className={styles.gwpValueRow} key={gas}>
                <span>{getGasDisplayName(gas)}</span>
                <strong>{String(value)}</strong>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const getFormalActivityNameMeta = (activity, t) => ({
  subtitle: getEmissionSourceLabel(activity, t),
  searchValues: [
    activity?.name,
    activity?.emissionSourceName,
    activity?.efName,
  ],
});

const ActivityInfoTooltip = ({ activity, year }) => (
  <MuiTooltip
    title={<ActivityTooltipContent activity={activity} year={year} />}
    placement="top-end"
    arrow
    enterDelay={150}
    slotProps={{
      popper: { sx: { zIndex: 5000 } },
      tooltip: {
        sx: {
          maxWidth: 340,
          bgcolor: "#101828",
          boxShadow: "0 10px 26px rgba(15, 23, 42, 0.22)",
          fontSize: 11,
          fontWeight: 500,
          lineHeight: 1.45,
        },
      },
      arrow: { sx: { color: "#101828" } },
    }}
  >
    <span
      className={styles.activityOptionInfo}
      aria-label="Emission factor details"
      onClick={(event) => event.stopPropagation()}
    >
      i
    </span>
  </MuiTooltip>
);

const ActivityNameDropdown = ({
  value,
  options,
  yearType,
  year,
  onChange,
  disabled = false,
  placeholder,
  emptyMessage = "No data",
}) => {
  const { t } = useTranslation("ActivityMain");
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const selectRef = useRef(null);
  const selected = options.find(
    (activity) => String(activity.activityId) === String(value),
  );
  const metaYear = year || yearType;
  const selectedMeta = getFormalActivityNameMeta(selected, t);
  const filteredOptions = useMemo(() => {
    const keyword = searchTerm.trim().toLowerCase();
    if (!keyword) return options;

    return options.filter((activity) => {
      const meta = getFormalActivityNameMeta(activity, t);
      return meta.searchValues
        .filter(Boolean)
        .some((text) => String(text).toLowerCase().includes(keyword));
    });
  }, [options, searchTerm, t]);

  const handleBlur = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setOpen(false);
      setSearchTerm("");
    }
  };

  useLayoutEffect(() => {
    if (!open || !selectRef.current) return;

    const rect = selectRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    setOpenUp(spaceBelow < 340 && spaceAbove > spaceBelow);
  }, [open, filteredOptions.length]);

  return (
    <div className={styles.activitySelect} onBlur={handleBlur} ref={selectRef}>
      <div className={styles.activitySelectControl}>
        <button
          type="button"
          className={styles.activitySelectButton}
          aria-haspopup="listbox"
          aria-expanded={open}
          disabled={disabled}
          onClick={() => {
            if (disabled) return;
            setOpen((prev) => {
              if (prev) setSearchTerm("");
              return !prev;
            });
          }}
        >
          {selected ? (
            <span className={styles.activityOptionContent}>
              <span className={styles.activityOptionText}>
                <span className={styles.activityOptionName}>
                  <span className={styles.activityOptionNameText}>
                    {selected.name}
                  </span>
                  <ActivityStatusTags activity={selected} t={t} />
                </span>
                <span className={styles.activityOptionMeta}>
                  {selectedMeta.subtitle}
                </span>
              </span>
              <ActivityInfoTooltip activity={selected} year={metaYear} />
            </span>
          ) : (
            <span className={styles.simpleSelectValue}>
              {placeholder || t("Select")}
            </span>
          )}
          <span className={styles.selectChevron} aria-hidden="true" />
        </button>
      </div>

      {open && !disabled && (
        <div
          className={`${styles.activitySelectMenu} ${
            openUp ? styles.activitySelectMenuOpenUp : ""
          } ${styles.activitySearchMenu}`}
          role="listbox"
        >
          <div className={styles.activitySearchWrap}>
            <input
              className={styles.activitySearchInput}
              value={searchTerm}
              placeholder={t("Search by name or source")}
              onChange={(event) => setSearchTerm(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  setOpen(false);
                  setSearchTerm("");
                }
              }}
            />
          </div>
          <div className={styles.activitySelectOptionList}>
            {filteredOptions.length ? (
              filteredOptions.map((activity) => {
                const meta = getFormalActivityNameMeta(activity, t);
                const active =
                  String(activity.activityId) === String(selected?.activityId);
                const incomplete = !activity.hasRawData || !activity.hasEF;
                return (
                  <button
                    key={activity.activityId || activity.name}
                    type="button"
                    role="option"
                    aria-selected={active}
                    className={`${styles.activitySelectOption} ${
                      active ? styles.activitySelectOptionActive : ""
                    } ${incomplete ? styles.activitySelectOptionIncomplete : ""}`}
                    onClick={() => {
                      onChange(activity.activityId);
                      setOpen(false);
                      setSearchTerm("");
                    }}
                  >
                    <span className={styles.activityOptionContent}>
                      <span className={styles.activityOptionText}>
                        <span className={styles.activityOptionName}>
                          <span className={styles.activityOptionNameText}>
                            {activity.name}
                          </span>
                          <ActivityStatusTags activity={activity} t={t} />
                        </span>
                        <span className={styles.activityOptionMeta}>
                          {meta.subtitle}
                        </span>
                      </span>
                      <ActivityInfoTooltip activity={activity} year={metaYear} />
                    </span>
                  </button>
                );
              })
            ) : (
              <div className={styles.activitySelectEmpty}>
                {t(emptyMessage)}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const RMNoActivityState = ({ title, description }) => (
  <div className={styles.noActivityState}>
    <strong>{title}</strong>
    <span>{description}</span>
  </div>
);

const RMStandardGate = ({ context, modalTitle, onClose }) => {
  const { t } = useTranslation("ActivityMain");
  const checking = context.status === RM_STANDARD_STATUS.CHECKING;
  const mismatch = context.status === RM_STANDARD_STATUS.MISMATCH;

  return (
    <div className={styles.modalBackdrop}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>{t(modalTitle)}</h2>
          <div className={styles.modalActions}>
            <button
              type="button"
              className={styles.primaryButton}
              disabled
            >
              {t("Save Activity")}
            </button>
            <button
              type="button"
              className={styles.closeButton}
              onClick={onClose}
              aria-label={t("Close")}
            >
              <CloseIcon />
            </button>
          </div>
        </div>
        {checking ? (
          <div className={styles.standardChecking} role="status">
            <span className={styles.loadingSpinner} aria-hidden="true" />
            <span>{t("Checking Reporting Standard...")}</span>
          </div>
        ) : (
          <div className={styles.standardBlocked}>
            <div className={styles.standardBlockedIcon} aria-hidden="true">
              !
            </div>
            <h3>{t("Emission Removal & Mitigation is unavailable")}</h3>
            <p>
              {t(
                mismatch
                  ? "The Base Year and the Reporting Year use different Reporting Standards. Go to Boundary Settings to change the Base Year, or its Reporting Standard."
                  : "Boundary Settings are incomplete. Complete the Base Year and Reporting Standard settings before creating an R&M activity.",
              )}
            </p>
            {mismatch && (
              <div className={styles.standardComparison}>
                {t(
                  "Base Year {{baseYear}} uses {{baseStandard}}. Reporting Year {{reportingYear}} uses {{reportingStandard}}.",
                  {
                    baseYear: context.baseYear,
                    baseStandard: t(getStandardLabel(context.baseStandardId)),
                    reportingYear: context.reportingYear,
                    reportingStandard: t(
                      getStandardLabel(context.reportingStandardId),
                    ),
                  },
                )}
              </div>
            )}
            <button
              type="button"
              className={styles.standardCloseButton}
              onClick={onClose}
            >
              {t("Close")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const SCOPE_CHART_COLORS = {
  1: {
    label: "Scope 1",
    solid: "#0f766e",
    light: "rgba(15, 118, 110, 0.28)",
  },
  2: {
    label: "Scope 2",
    solid: "#2563eb",
    light: "rgba(37, 99, 235, 0.25)",
  },
  3: {
    label: "Scope 3",
    solid: "#b45309",
    light: "rgba(180, 83, 9, 0.25)",
  },
};

const formatRMOptional = (value) =>
  isRMEmptyTableValue(value) ? "-" : formatRMNumber(value);

const formatSignedChange = (value) => {
  const number = Number(value) || 0;
  if (!number) return "-";
  return `${number > 0 ? "+" : "-"}${formatRMNumber(Math.abs(number))}`;
};

const hasOwnValue = (item, key) =>
  Object.prototype.hasOwnProperty.call(item || {}, key);

const formatRMDisplayCategory = (category) =>
  isRMEmptyTableValue(category) ? "-" : String(category).replaceAll("_", ".");

const formatRMDisplayEmission = (value) =>
  isRMEmptyTableValue(value) ? "-" : formatRMNumber(value);

const formatReplacementPct = (activity) => {
  if (activity?.scenario === "S3") return "-";

  const value =
    activity?.replacement ??
    activity?.replacementPct ??
    activity?.editInfo?.replacement;
  const number = Number(value);

  return Number.isFinite(number)
    ? number.toLocaleString(undefined, { maximumFractionDigits: 2 })
    : "-";
};

const DeleteRMConfirmModal = ({ activity, deleting, onCancel, onConfirm }) => {
  const { t } = useTranslation("ActivityMain");
  const activityName = formatRMTableText(
    activity?.itemName || activity?.name,
  );

  return (
    <div className={styles.confirmBackdrop} role="presentation">
      <div
        className={styles.confirmDialog}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="delete-rm-title"
      >
        <div className={styles.confirmIcon} aria-hidden="true">
          <DeleteOutlineIcon />
        </div>
        <h3 id="delete-rm-title">{t("Delete this R&M activity?")}</h3>
        <p>
          {t(
            '"{{activityName}}" and its calculated result will be removed. This cannot be undone.',
            { activityName },
          )}
        </p>
        <div className={styles.confirmActions}>
          <button
            type="button"
            className={styles.confirmCancelButton}
            onClick={onCancel}
            disabled={deleting}
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            className={styles.confirmDeleteButton}
            onClick={onConfirm}
            disabled={deleting}
          >
            {t(deleting ? "Deleting..." : "Delete activity")}
          </button>
        </div>
      </div>
    </div>
  );
};

const RMActivityTable = observer(({ activities }) => {
  const { t } = useTranslation("ActivityMain");
  const store = emissionViewStore;
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!deleteTarget?.id || deleting) return;

    setDeleting(true);
    const deleted = await store.deleteRMActivityData(deleteTarget.id);
    setDeleting(false);
    if (deleted) setDeleteTarget(null);
  };

  return (
    <>
      <div className={styles.tableWrap}>
        <table className={styles.rmTable}>
          <thead>
            <tr>
              <th>{t("No.")}</th>
              <th>{t("Entity")}</th>
              <th>{t("Activity")}</th>
              <th>{t("R&M Type")}</th>
              <th>{t("Scenario")}</th>
              <th>{t("Before Scope/Category")}</th>
              <th>{t("Reduced Emission (tCO₂e)")}</th>
              <th>{t("After Scope/Category")}</th>
              <th>{t("Increased Emission (tCO₂e)")}</th>
              <th>{t("Repl %")}</th>
              <th>{t("Action")}</th>
            </tr>
          </thead>
          <tbody>
            {!activities.length ? (
              <tr>
                <td colSpan="11" className={styles.empty}>
                  {t("No data!")}
                </td>
              </tr>
            ) : (
              activities.map((activity, index) => {
              const result = calculateRMActivity(activity);
              const entityName = formatRMTableText(
                activity.companyName || activity.entity,
              );
              const activityName = formatRMTableText(
                activity.itemName || activity.name,
              );
              const rmType = formatRMTableText(
                activity.activityName || formatRMActivityType(activity.rmType),
              );
              const beforeCategory = activity.beforeCategory;
              const afterCategory = activity.afterCategory;
              const reducedEmission = hasOwnValue(activity, "reducedEmission")
                ? activity.reducedEmission
                : result.reduced;
              const increasedEmission = hasOwnValue(
                activity,
                "increasedEmission",
              )
                ? activity.increasedEmission
                : result.increased;
                return (
                  <tr key={activity.id}>
                  <td>
                    {(store.rmActivityListData.page - 1) *
                      store.rmActivityListData.pageSize +
                      index +
                      1}
                  </td>
                  <td>
                    <div className={styles.entityCell}>
                      <strong>{entityName}</strong>
                      {!isRMEmptyTableValue(activity.hierarchyNamePath) && (
                        <span className={styles.entityPath}>
                          {activity.hierarchyNamePath}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <strong>{activityName}</strong>
                  </td>
                  <td>{rmType === "-" ? rmType : t(rmType)}</td>
                  <td>
                    <ScenarioChip scenario={activity.scenario} />
                  </td>
                  <td>{formatRMDisplayCategory(beforeCategory)}</td>
                  <td
                    className={`${styles.numberCell} ${styles.reducedValue}`}
                  >
                    {formatRMDisplayEmission(reducedEmission)}
                  </td>
                  <td>{formatRMDisplayCategory(afterCategory)}</td>
                  <td
                    className={`${styles.numberCell} ${styles.increasedValue}`}
                  >
                    {formatRMDisplayEmission(increasedEmission)}
                  </td>
                  <td className={styles.numberCell}>
                    {formatReplacementPct(activity)}
                  </td>
                  <td>
                    <div className={styles.actionGroup}>
                      <MuiTooltip title={t("Edit")} arrow>
                        <span>
                          <button
                            type="button"
                            className={`${styles.iconActionButton} ${styles.editActionButton}`}
                            onClick={() =>
                              store._handleShowRMActivityEditModal(activity)
                            }
                            disabled={!activity.editInfo}
                            aria-label={t("Edit activity")}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </button>
                        </span>
                      </MuiTooltip>
                      <MuiTooltip title={t("Delete")} arrow>
                        <button
                          type="button"
                          className={`${styles.iconActionButton} ${styles.deleteActionButton}`}
                          onClick={() => setDeleteTarget(activity)}
                          aria-label={t("Delete activity")}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </button>
                      </MuiTooltip>
                    </div>
                  </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {deleteTarget && (
        <DeleteRMConfirmModal
          activity={deleteTarget}
          deleting={deleting}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
        />
      )}
    </>
  );
});

const RMReductionView = ({ activities }) => {
  const { t } = useTranslation("ActivityMain");

  return (
    <div className={styles.container}>
      <div className={styles.panel}>
        <div className={styles.panelHeader}>
          <div>
            <h3 className={styles.panelTitle}>{t("Emission Reduction")}</h3>
            <p className={styles.panelSub}>
              {t("Calculated result per activity with formulas.")}
            </p>
          </div>
        </div>
        <div className={styles.panelBody}>
          <div className={styles.tableWrap}>
            <table className={styles.rmTable}>
              <thead>
                <tr>
                  <th>{t("No.")}</th>
                  <th>{t("Activity")}</th>
                  <th>{t("Scenario")}</th>
                  <th>{t("Before Scope/Category")}</th>
                  <th>{t("Before Source")}</th>
                  <th>{t("Reduced Activity Data")}</th>
                  <th>{t("Reduced Emission (tCO₂e)")}</th>
                  <th>{t("After Scope/Category")}</th>
                  <th>{t("After Source")}</th>
                  <th>{t("Increased Emission (tCO₂e)")}</th>
                </tr>
              </thead>
              <tbody>
                {!activities.length ? (
                  <tr>
                    <td colSpan="10" className={styles.empty}>
                      {t("No data")}
                    </td>
                  </tr>
                ) : (
                  activities.map((activity, index) => {
                    const result = calculateRMActivity(activity);
                    const activityName = formatRMTableText(
                      activity.itemName || activity.name,
                    );
                    const reducedEmission = hasOwnValue(
                      activity,
                      "reducedEmission",
                    )
                      ? activity.reducedEmission
                      : result.reduced;
                    const increasedEmission = hasOwnValue(
                      activity,
                      "increasedEmission",
                    )
                      ? activity.increasedEmission
                      : result.increased;
                    const beforeSource = formatRMTableText(
                      activity.beforeEFName || activity.beforeEFname,
                    );
                    const afterSource = formatRMTableText(
                      activity.afterEFName || activity.afterEFname,
                    );
                    return (
                      <tr key={activity.id}>
                        <td>{index + 1}</td>
                        <td>
                          <strong>{activityName}</strong>
                        </td>
                        <td>
                          <ScenarioChip scenario={activity.scenario} />
                        </td>
                        <td>
                          {formatRMDisplayCategory(activity.beforeCategory)}
                        </td>
                        <td>{beforeSource}</td>
                        <td className={styles.numberCell}>
                          {activity.scenario === "S3" &&
                          !isRMEmptyTableValue(activity.reducedActivityData)
                            ? formatRMNumber(activity.reducedActivityData, 2)
                            : "-"}
                        </td>
                        <td
                          className={`${styles.numberCell} ${styles.reducedValue}`}
                        >
                          {formatRMDisplayEmission(reducedEmission)}
                        </td>
                        <td>
                          {formatRMDisplayCategory(activity.afterCategory)}
                        </td>
                        <td>{afterSource}</td>
                        <td
                          className={`${styles.numberCell} ${styles.increasedValue}`}
                        >
                          {formatRMDisplayEmission(increasedEmission)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

const SummaryRows = ({ rows, scenario }) => {
  const { t } = useTranslation("ActivityMain");

  if (!rows.length) {
    return (
      <tr>
        <td colSpan={scenario ? 6 : 5} className={styles.empty}>
          {t("No data")}
        </td>
      </tr>
    );
  }

  return rows.map((row) => (
    <tr key={row.key}>
      <td>
        {scenario ? (
          <ScenarioChip scenario={row.key} />
        ) : (
          formatRMTableText(row.key)
        )}
      </td>
      {scenario && (
        <td>
          {isRMEmptyTableValue(row.description) ? "-" : t(row.description)}
        </td>
      )}
      <td>{formatRMTableText(row.activities)}</td>
      <td className={`${styles.numberCell} ${styles.reducedValue}`}>
        {formatRMOptional(row.reduced)}
      </td>
      <td className={`${styles.numberCell} ${styles.increasedValue}`}>
        {formatRMOptional(row.increased)}
      </td>
      <td className={`${styles.numberCell} ${styles.netValue}`}>
        {formatRMOptional(row.net)}
      </td>
    </tr>
  ));
};

const EntityChart = ({ summary }) => {
  const { t } = useTranslation("ActivityMain");
  const labels = summary.byEntity.map((item) => item.key);
  const datasets = [];

  RM_SCOPE_OPTIONS.forEach((scope) => {
    const colors = SCOPE_CHART_COLORS[scope];
    const reducedData = summary.byEntity.map(
      (item) => item.scopeBreakdown[scope]?.reduced || 0,
    );
    const increasedData = summary.byEntity.map(
      (item) => item.scopeBreakdown[scope]?.increased || 0,
    );

    if (reducedData.some(Boolean)) {
      datasets.push({
        label: `${t(colors.label)} - ${t("Reduced")}`,
        data: reducedData,
        backgroundColor: colors.solid,
        borderColor: colors.solid,
        borderWidth: 1,
        borderRadius: 3,
        stack: `scope-${scope}`,
      });
    }

    if (increasedData.some(Boolean)) {
      datasets.push({
        label: `${t(colors.label)} - ${t("Increased")}`,
        data: increasedData,
        backgroundColor: colors.light,
        borderColor: colors.solid,
        borderWidth: 2,
        borderDash: [5, 3],
        borderRadius: 3,
        stack: `increase-${scope}`,
      });
    }
  });

  datasets.push({
    type: "line",
    label: t("Net (all scopes)"),
    data: summary.byEntity.map((item) => item.net),
    backgroundColor: "rgba(0, 0, 0, 0)",
    borderColor: "#18181b",
    borderDash: [6, 3],
    borderWidth: 2,
    pointBackgroundColor: "#18181b",
    pointRadius: 4,
    tension: 0,
    order: 0,
  });

  return (
    <div className={styles.chartBox}>
      <Bar
        data={{ labels, datasets }}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: "bottom",
              labels: {
                boxWidth: 12,
                boxHeight: 12,
                usePointStyle: false,
                font: { size: 13 },
                padding: 16,
              },
            },
            tooltip: {
              titleFont: { size: 13 },
              bodyFont: { size: 13 },
              callbacks: {
                label: (context) => {
                  const value = context.parsed?.y ?? context.parsed;
                  return `${context.dataset.label}: ${formatRMNumber(value)} tCO2e`;
                },
              },
            },
          },
          scales: {
            x: {
              grid: { display: false },
              ticks: { font: { size: 13 } },
            },
            y: {
              grid: { color: "rgba(16, 24, 40, 0.08)" },
              ticks: { font: { size: 13 } },
              title: {
                display: true,
                text: "tCO2e",
                font: { size: 13 },
              },
            },
          },
        }}
      />
    </div>
  );
};

const ScopeDetailRows = ({ summary }) => {
  const { t } = useTranslation("ActivityMain");

  if (!summary.scopeDetail.length) {
    return (
      <tr>
        <td colSpan="5" className={styles.empty}>
          {t("No data")}
        </td>
      </tr>
    );
  }

  return summary.scopeDetail.flatMap((entity) => {
    const rows = entity.scopes.map((scope, index) => (
      <tr key={`${entity.entity}-${scope.scope}`}>
        {index === 0 && (
          <td rowSpan={entity.scopes.length} className={styles.entityTotalCell}>
            {formatRMTableText(entity.entity)}
          </td>
        )}
        <td>{isRMEmptyTableValue(scope.label) ? "-" : t(scope.label)}</td>
        <td className={`${styles.numberCell} ${styles.reducedValue}`}>
          {formatRMOptional(scope.reduced)}
        </td>
        <td className={`${styles.numberCell} ${styles.increasedValue}`}>
          {formatRMOptional(scope.increased)}
        </td>
        <td className={`${styles.numberCell} ${styles.netValue}`}>
          {formatRMOptional(scope.net)}
        </td>
      </tr>
    ));

    rows.push(
      <tr key={`${entity.entity}-total`} className={styles.totalRow}>
        <td colSpan="2">
          {formatRMTableText(entity.entity)} - {t("Total")}
        </td>
        <td className={`${styles.numberCell} ${styles.reducedValue}`}>
          {formatRMOptional(entity.total.reduced)}
        </td>
        <td className={`${styles.numberCell} ${styles.increasedValue}`}>
          {formatRMOptional(entity.total.increased)}
        </td>
        <td className={`${styles.numberCell} ${styles.netValue}`}>
          {formatRMOptional(entity.total.net)}
        </td>
      </tr>,
    );

    return rows;
  });
};

const SummaryEmissionUnit = () => (
  <span className={styles.summaryEmissionUnit}>tCO₂e</span>
);

const RMSummaryView = ({ activities }) => {
  const { t } = useTranslation("ActivityMain");
  const [entityView, setEntityView] = useState("chart");
  const [activityPage, setActivityPage] = useState(1);
  const summary = getRMSummary(activities);
  const entityCount = summary.byEntity.length;
  const activityPageCount = Math.max(
    1,
    Math.ceil(summary.rows.length / RM_SUMMARY_PAGE_SIZE),
  );
  const visibleActivityPage = Math.min(activityPage, activityPageCount);
  const activityStartIndex =
    (visibleActivityPage - 1) * RM_SUMMARY_PAGE_SIZE;
  const paginatedActivities = summary.rows.slice(
    activityStartIndex,
    activityStartIndex + RM_SUMMARY_PAGE_SIZE,
  );
  const activityRangeStart = summary.rows.length ? activityStartIndex + 1 : 0;
  const activityRangeEnd = Math.min(
    activityStartIndex + RM_SUMMARY_PAGE_SIZE,
    summary.rows.length,
  );
  const kpiSubtitles = {
    "Total Reduced Emission": "tCO₂e",
    "Total Increased Emission": "tCO₂e (S1 only)",
    "Net Emission Reduction": "tCO₂e (Reduced − Increased)",
  };
  const kpiToneClasses = {
    "Total Activities": styles.kpiActivities,
    "Total Reduced Emission": styles.kpiReduced,
    "Total Increased Emission": styles.kpiIncreased,
    "Net Emission Reduction": styles.kpiNet,
  };

  const renderKpiSubtitle = (label) => {
    if (label === "Total Activities") {
      const entityLabel = entityCount === 1 ? t("entity") : t("entities");
      return `${t("Across")} ${entityCount} ${entityLabel}`;
    }

    return t(kpiSubtitles[label]);
  };

  useEffect(() => {
    setActivityPage(1);
  }, [activities]);

  return (
    <div className={`${styles.container} ${styles.summaryContainer}`}>
      <div className={`${styles.panel} ${styles.summaryPanel}`}>
        <div className={styles.panelHeader}>
          <div>
            <h3 className={styles.panelTitle}>{t("Summary")}</h3>
            <p className={styles.panelSub}>
              {t(
                "Emission reduction results overview across all scenarios and entities",
              )}
            </p>
          </div>
        </div>
        <div className={styles.panelBody}>
          <div className={styles.kpiGrid}>
            {summary.kpis.map((kpi) => (
              <div
                className={`${styles.kpiCard} ${kpiToneClasses[kpi.label]}`}
                key={kpi.label}
              >
                <div className={styles.kpiLabel}>{t(kpi.label)}</div>
                <div className={styles.kpiValue}>{kpi.value}</div>
                <div className={styles.panelSub}>
                  {renderKpiSubtitle(kpi.label)}
                </div>
              </div>
            ))}
          </div>

          <div className={styles.summaryGrid}>
            <div className={styles.summaryCard}>
              <div className={styles.summaryCardTitle}>
                {t("BREAKDOWN BY SCENARIO")}
              </div>
              <div className={styles.tableWrap}>
                <table className={styles.rmTable}>
                  <thead>
                    <tr>
                      <th>{t("SCENARIO")}</th>
                      <th>{t("DESCRIPTION")}</th>
                      <th>{t("ACTIVITIES")}</th>
                      <th className={styles.reducedHeader}>
                        {t("REDUCED EMISSION")} (<SummaryEmissionUnit />)
                      </th>
                      <th className={styles.increasedHeader}>
                        {t("INCREASED EMISSION")} (<SummaryEmissionUnit />)
                      </th>
                      <th className={styles.netHeader}>
                        {t("NET")} (<SummaryEmissionUnit />)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <SummaryRows rows={summary.byScenario} scenario />
                  </tbody>
                </table>
              </div>
            </div>

            <div className={styles.summaryCard}>
              <div className={styles.summaryCardHeader}>
                <div className={styles.summaryCardTitle}>
                  {t("ENTITY COMPARISON")}
                </div>
                <div className={styles.viewToggle}>
                  <span>{t("View")}:</span>
                  <button
                    type="button"
                    className={`${styles.toggleButton} ${
                      entityView === "chart" ? styles.activeToggle : ""
                    }`}
                    onClick={() => setEntityView("chart")}
                  >
                    {t("Chart")}
                  </button>
                  <button
                    type="button"
                    className={`${styles.toggleButton} ${
                      entityView === "table" ? styles.activeToggle : ""
                    }`}
                    onClick={() => setEntityView("table")}
                  >
                    {t("Table")}
                  </button>
                </div>
              </div>
              {entityView === "chart" ? (
                <div className={styles.entityChartView}>
                  <EntityChart summary={summary} />
                  <div className={styles.scopeDetail}>
                    <div className={styles.scopeDetailTitle}>
                      {t("SCOPE BREAKDOWN PER ENTITY")}
                    </div>
                    <div className={styles.tableWrap}>
                      <table className={styles.rmTable}>
                        <thead>
                          <tr>
                            <th>{t("ENTITY")}</th>
                            <th>{t("SCOPE")}</th>
                            <th className={styles.reducedHeader}>
                              {t("REDUCED")} (<SummaryEmissionUnit />)
                            </th>
                            <th className={styles.increasedHeader}>
                              {t("INCREASED")} (<SummaryEmissionUnit />)
                            </th>
                            <th className={styles.netHeader}>
                              {t("NET")} (<SummaryEmissionUnit />)
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <ScopeDetailRows summary={summary} />
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ) : (
                <div className={styles.tableWrap}>
                  <table className={styles.rmTable}>
                    <thead>
                      <tr>
                        <th>{t("REPORTING ENTITY")}</th>
                        <th>{t("ACTIVITIES")}</th>
                        <th className={styles.reducedHeader}>
                          {t("REDUCED EMISSION")} (<SummaryEmissionUnit />)
                        </th>
                        <th className={styles.increasedHeader}>
                          {t("INCREASED EMISSION")} (<SummaryEmissionUnit />)
                        </th>
                        <th className={styles.netHeader}>
                          {t("NET")} (<SummaryEmissionUnit />)
                        </th>
                        <th>{t("SHARE OF TOTAL REDUCTION")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.byEntity.map((row) => {
                      const shareValue = summary.totals.totalReduced
                        ? (row.reduced / summary.totals.totalReduced) * 100
                        : 0;
                      const share = isRMEmptyTableValue(shareValue)
                        ? "-"
                        : `${shareValue.toFixed(1)}%`;
                      const shareWidth = summary.totals.totalReduced
                        ? `${Math.max(0, Math.min(100, shareValue))}%`
                        : "0.0%";

                      return (
                        <tr key={row.key}>
                          <td>{formatRMTableText(row.key)}</td>
                          <td>{formatRMTableText(row.activities)}</td>
                          <td
                            className={`${styles.numberCell} ${styles.reducedValue}`}
                          >
                            {formatRMOptional(row.reduced)}
                          </td>
                          <td
                            className={`${styles.numberCell} ${styles.increasedValue}`}
                          >
                            {formatRMOptional(row.increased)}
                          </td>
                          <td
                            className={`${styles.numberCell} ${styles.netValue}`}
                          >
                            {formatRMOptional(row.net)}
                          </td>
                          <td>
                            <div className={styles.shareBar}>
                              <span
                                style={{
                                  width: shareWidth,
                                }}
                              />
                            </div>
                            <span className={styles.shareLabel}>{share}</span>
                          </td>
                        </tr>
                      );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div className={styles.summaryCard} style={{ marginTop: 12 }}>
            <div className={styles.summaryCardTitle}>
              {t("BREAKDOWN BY SCOPE / CATEGORY")}
            </div>
            <div className={styles.tableWrap}>
              <table className={styles.rmTable}>
                <thead>
                  <tr>
                    <th>{t("SCOPE / CATEGORY")}</th>
                    <th>{t("DIRECTION")}</th>
                    <th>{t("ACTIVITIES")}</th>
                    <th className={styles.emissionHeader}>
                      {t("EMISSION")} (<SummaryEmissionUnit />)
                    </th>
                    <th className={styles.netHeader}>
                      {t("NET CHANGE")} (<SummaryEmissionUnit />)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {summary.byScope.map((row) => (
                  <tr
                    key={row.key}
                    className={row.isTotal ? styles.totalRow : undefined}
                  >
                    <td className={row.isTotal ? styles.totalLabel : undefined}>
                      {formatRMTableText(row.label)}
                    </td>
                    <td>
                      {isRMEmptyTableValue(row.direction)
                        ? "-"
                        : t(row.direction)}
                    </td>
                    <td>{formatRMTableText(row.activities)}</td>
                    <td
                      className={`${styles.numberCell} ${
                        row.direction === "Reduced"
                          ? styles.reducedValue
                          : row.direction === "Increased"
                            ? styles.increasedValue
                            : ""
                      }`}
                    >
                      {row.isTotal
                        ? `${t("Reduced")} ${formatRMOptional(
                            row.reduced,
                          )} / ${t("Increased")} ${formatRMOptional(
                            row.increased,
                          )}`
                        : formatRMOptional(row.emission)}
                    </td>
                    <td className={`${styles.numberCell} ${styles.netValue}`}>
                      {formatSignedChange(row.netChange)}
                    </td>
                  </tr>
                  ))}
                  {!summary.byScope.length && (
                    <tr>
                      <td colSpan="5" className={styles.empty}>
                        {t("No data")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className={styles.summaryCard} style={{ marginTop: 12 }}>
            <div className={styles.summaryCardTitle}>{t("ALL ACTIVITIES")}</div>
            <div className={styles.tableWrap}>
              <table className={styles.rmTable}>
                <thead>
                  <tr>
                    <th>{t("NO.")}</th>
                    <th>{t("ENTITY")}</th>
                    <th>{t("ACTIVITY NAME")}</th>
                    <th>{t("R&M ACTIVITY")}</th>
                    <th>{t("SCENARIO")}</th>
                    <th>{t("REDUCED SCOPE/CAT")}</th>
                    <th className={styles.reducedHeader}>
                      {t("REDUCED EMISSION")} (<SummaryEmissionUnit />)
                    </th>
                    <th>{t("INCREASED SCOPE/CAT")}</th>
                    <th className={styles.increasedHeader}>
                      {t("INCREASED EMISSION")} (<SummaryEmissionUnit />)
                    </th>
                    <th className={styles.netHeader}>
                      {t("NET")} (<SummaryEmissionUnit />)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedActivities.map((row, index) => (
                  <tr key={row.id}>
                    <td>{activityStartIndex + index + 1}</td>
                    <td>{formatRMTableText(row.entity)}</td>
                    <td>{formatRMTableText(row.name)}</td>
                    <td>
                      {isRMEmptyTableValue(row.rmType)
                        ? "-"
                        : t(formatRMActivityType(row.rmType) || row.rmType)}
                    </td>
                    <td>
                      <ScenarioChip scenario={row.scenario} />
                    </td>
                    <td>{formatRMDisplayCategory(row.beforeCategory)}</td>
                    <td className={`${styles.numberCell} ${styles.reducedValue}`}>
                      {formatRMOptional(row.result.reduced)}
                    </td>
                    <td>{formatRMDisplayCategory(row.afterCategory)}</td>
                    <td
                      className={`${styles.numberCell} ${styles.increasedValue}`}
                    >
                      {formatRMOptional(row.result.increased)}
                    </td>
                    <td className={`${styles.numberCell} ${styles.netValue}`}>
                      {formatRMOptional(row.result.net)}
                    </td>
                  </tr>
                  ))}
                  {!summary.rows.length && (
                    <tr>
                      <td colSpan="10" className={styles.empty}>
                        {t("No data")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div
              className={styles.activityPagination}
              role="navigation"
              aria-label={t("All Activities pagination")}
            >
              <button
                type="button"
                className={styles.paginationButton}
                onClick={() =>
                  setActivityPage((page) => Math.max(1, page - 1))
                }
                disabled={visibleActivityPage === 1}
                aria-label={t("Previous page")}
              >
                <ChevronLeftIcon fontSize="small" />
              </button>
              <span className={styles.paginationRange} aria-live="polite">
                {activityRangeStart}-{activityRangeEnd} {t("of")}{" "}
                {summary.rows.length}
              </span>
              <button
                type="button"
                className={styles.paginationButton}
                onClick={() =>
                  setActivityPage((page) =>
                    Math.min(activityPageCount, page + 1),
                  )
                }
                disabled={visibleActivityPage === activityPageCount}
                aria-label={t("Next page")}
              >
                <ChevronRightIcon fontSize="small" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const RMActivityModal = observer(() => {
  const { t } = useTranslation("ActivityMain");
  const store = emissionViewStore;
  const rootId = commonStore.rootId;
  const userId = commonStore.userId;
  const editActivity = store.rmActivityEditData;
  const editInfo = editActivity?.editInfo;
  const isEditMode = Boolean(editActivity?.id && editInfo);

  const entityOptions = useMemo(() => {
    const seen = new Set();
    const options =
      commonStore.scopeType === "COMPANY"
        ? getSelectComOptionByCompanyList(
            store.inventoryCompanyList,
            store.currentCId,
            commonStore.role,
          )
        : getSelectComOptionByOrgList(
            commonStore.orgList,
            store.currentCId,
            commonStore.rootId,
          );

    return options.filter((item) => {
      if (!item.value || !item.label || seen.has(item.value)) return false;
      seen.add(item.value);
      return true;
    });
  }, [
    store.inventoryCompanyList,
    commonStore.orgList,
    commonStore.role,
    commonStore.rootId,
    commonStore.scopeType,
    store.currentCId,
  ]);

  const rmActivityTypeOptions = useMemo(
    () =>
      RM_ACTIVITY_TYPES.map((option) => ({
        ...option,
        label: t(option.label),
      })),
    [t],
  );

  const [form, setForm] = useState(() => getInitialRMForm(editActivity));
  const [boundaryDropdownData, setBoundaryDropdownData] = useState(null);
  const [boundaryDropdownLoading, setBoundaryDropdownLoading] = useState(false);
  const [beforeActivities, setBeforeActivities] = useState([]);
  const [beforeActivitiesLoading, setBeforeActivitiesLoading] = useState(false);
  const [loadedBeforeActivitiesKey, setLoadedBeforeActivitiesKey] =
    useState("");
  const [afterActivities, setAfterActivities] = useState([]);
  const [afterActivitiesLoading, setAfterActivitiesLoading] = useState(false);
  const [loadedAfterActivitiesKey, setLoadedAfterActivitiesKey] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!entityOptions.length) return;

    setForm((prev) => {
      if (!prev.companyId) return prev;

      const current = entityOptions.find(
        (item) => item.value === prev.companyId,
      );
      if (current) {
        return current.label === prev.entity
          ? prev
          : { ...prev, entity: current.label };
      }

      return {
        ...prev,
        companyId: "",
        entity: "",
      };
    });
  }, [entityOptions]);

  const modalReportingYear = Number(
    editInfo?.reportingYear || store.selectedYear || 0,
  );
  const [standardContext, setStandardContext] = useState({
    status: RM_STANDARD_STATUS.CHECKING,
    reportingYear: modalReportingYear,
  });

  useEffect(() => {
    if (
      !rootId ||
      !userId ||
      !modalReportingYear
    ) {
      setStandardContext({
        status: RM_STANDARD_STATUS.UNAVAILABLE,
        reportingYear: modalReportingYear,
      });
      return;
    }

    let active = true;
    setStandardContext({
      status: RM_STANDARD_STATUS.CHECKING,
      reportingYear: modalReportingYear,
    });

    fetchRMStandardContext({
      rootId,
      userId,
      reportingYear: modalReportingYear,
    })
      .then((context) => {
        if (active) setStandardContext(context);
      })
      .catch((err) => {
        console.error("Check R&M reporting standard failed:", err);
        if (active) {
          setStandardContext({
            status: RM_STANDARD_STATUS.UNAVAILABLE,
            reportingYear: modalReportingYear,
          });
        }
      });

    return () => {
      active = false;
    };
  }, [rootId, userId, modalReportingYear]);

  const standardCheckReady =
    standardContext.status === RM_STANDARD_STATUS.READY;

  useEffect(() => {
    if (
      !standardCheckReady ||
      !form.companyId ||
      !rootId ||
      !modalReportingYear
    ) {
      setBoundaryDropdownData(null);
      return;
    }

    let active = true;
    const requestBody = {
      companyId: form.companyId,
      rootId,
      year: modalReportingYear,
    };

    setBoundaryDropdownLoading(true);
    boundarySettingsAPI
      .getDecarbonCategoryDropdown(requestBody)
      .then((data) => {
        if (active) setBoundaryDropdownData(data || null);
      })
      .catch((err) => {
        console.error("Get decarbon category dropdown failed:", err);
        if (active) setBoundaryDropdownData(null);
      })
      .finally(() => {
        if (active) setBoundaryDropdownLoading(false);
      });

    return () => {
      active = false;
    };
  }, [
    standardCheckReady,
    form.companyId,
    rootId,
    modalReportingYear,
  ]);

  const baseYearConfig = useMemo(
    () =>
      boundaryDropdownData?.years?.find((item) => item.yearType === "baseYear"),
    [boundaryDropdownData],
  );
  const reportingYearConfig = useMemo(
    () =>
      boundaryDropdownData?.years?.find(
        (item) => item.yearType === "reportingYear",
      ),
    [boundaryDropdownData],
  );
  const hasBaseYearAssessmentMismatch = baseYearConfig?.hasAssessment === false;
  const boundaryStandardId = toFiniteNumberOrNull(
    boundaryDropdownData?.standardId,
  );
  const effectiveStandardId =
    boundaryStandardId ?? toFiniteNumberOrNull(editInfo?.standardId);

  const beforeDropdown = useMemo(() => {
    return baseYearConfig?.dropdown || {};
  }, [baseYearConfig]);

  const afterDropdown = useMemo(
    () => reportingYearConfig?.dropdown || {},
    [reportingYearConfig],
  );

  const beforeCategoryMap = useMemo(
    () => toCategoryMap(beforeDropdown),
    [beforeDropdown],
  );
  const afterCategoryMap = useMemo(
    () => toCategoryMap(afterDropdown),
    [afterDropdown],
  );

  useEffect(() => {
    if (!boundaryDropdownData) return;

    setForm((prev) =>
      normalizeForm(prev, {
        beforeCategoryMap,
        afterCategoryMap,
      }),
    );
  }, [boundaryDropdownData, beforeCategoryMap, afterCategoryMap]);

  const updateForm = (patch) => {
    const patchKeys = Object.keys(patch);
    if (patchKeys.length) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        const keysToClear = new Set(patchKeys);
        if (patchKeys.includes("beforeScope") || patchKeys.includes("q1")) {
          keysToClear.add("afterScope");
          keysToClear.add("afterCategory");
          keysToClear.add("afterActivity");
        }
        keysToClear.forEach((key) => {
          delete next[key];
        });
        return next;
      });
    }

    setForm((prev) =>
      normalizeForm(
        { ...prev, ...patch },
        {
          beforeCategoryMap,
          afterCategoryMap,
        },
      ),
    );
  };

  useEffect(() => {
    if (
      !form.companyId ||
      !form.beforeCategory ||
      !baseYearConfig?.year ||
      effectiveStandardId === null ||
      hasBaseYearAssessmentMismatch
    ) {
      setBeforeActivities([]);
      setBeforeActivitiesLoading(false);
      setLoadedBeforeActivitiesKey("");
      return;
    }

    let active = true;
    const requestKey = [
      form.companyId,
      form.beforeCategory,
      baseYearConfig.year,
      effectiveStandardId,
    ].join("|");
    const requestBody = {
      companyId: form.companyId,
      year: Number(baseYearConfig.year),
      category: form.beforeCategory,
      standardId: effectiveStandardId,
    };

    setBeforeActivitiesLoading(true);
    setLoadedBeforeActivitiesKey("");

    emissionActivityModalAPI
      .getRemovalEmissionList(requestBody)
      .then((data) => {
        if (!active) return;
        const nextActivities = (data || []).map(
          normalizeRemovalEmissionActivity,
        );
        setBeforeActivities(nextActivities);
        setLoadedBeforeActivitiesKey(requestKey);
        setForm((prev) => {
          const hasCurrent = nextActivities.some(
            (activity) =>
              String(activity.activityId) === String(prev.beforeActivity),
          );
          const nextActivity = hasCurrent ? prev.beforeActivity : "";
          return nextActivity === prev.beforeActivity
            ? prev
            : { ...prev, beforeActivity: nextActivity };
        });
      })
      .catch((err) => {
        console.error("Get removal before emission list failed:", err);
        if (active) {
          setBeforeActivities([]);
          setLoadedBeforeActivitiesKey(requestKey);
        }
      })
      .finally(() => {
        if (active) setBeforeActivitiesLoading(false);
      });

    return () => {
      active = false;
    };
  }, [
    form.companyId,
    form.beforeCategory,
    baseYearConfig?.year,
    boundaryDropdownData?.standardId,
    hasBaseYearAssessmentMismatch,
    effectiveStandardId,
  ]);

  useEffect(() => {
    if (
      !form.companyId ||
      !form.afterCategory ||
      !reportingYearConfig?.year ||
      effectiveStandardId === null
    ) {
      setAfterActivities([]);
      setAfterActivitiesLoading(false);
      setLoadedAfterActivitiesKey("");
      return;
    }

    let active = true;
    const requestKey = [
      form.companyId,
      form.afterCategory,
      reportingYearConfig.year,
      effectiveStandardId,
    ].join("|");
    const requestBody = {
      companyId: form.companyId,
      year: Number(reportingYearConfig.year),
      category: form.afterCategory,
      standardId: effectiveStandardId,
    };

    setAfterActivitiesLoading(true);
    setLoadedAfterActivitiesKey("");
    emissionActivityModalAPI
      .getRemovalEmissionList(requestBody)
      .then((data) => {
        if (!active) return;
        const nextActivities = (data || []).map(
          normalizeRemovalEmissionActivity,
        );
        setAfterActivities(nextActivities);
        setLoadedAfterActivitiesKey(requestKey);
        setForm((prev) => {
          const hasCurrent = nextActivities.some(
            (activity) =>
              String(activity.activityId) === String(prev.afterActivity),
          );
          const nextActivity = hasCurrent ? prev.afterActivity : "";
          return nextActivity === prev.afterActivity
            ? prev
            : { ...prev, afterActivity: nextActivity };
        });
      })
      .catch((err) => {
        console.error("Get removal after emission list failed:", err);
        if (active) {
          setAfterActivities([]);
          setLoadedAfterActivitiesKey(requestKey);
        }
      })
      .finally(() => {
        if (active) setAfterActivitiesLoading(false);
      });

    return () => {
      active = false;
    };
  }, [
    form.companyId,
    form.afterCategory,
    reportingYearConfig?.year,
    boundaryDropdownData?.standardId,
    effectiveStandardId,
  ]);

  const scenario = getRMScenario(form);
  const scenarioMeta = getRMScenarioMeta(scenario);
  const afterActivitiesKey = [
    form.companyId,
    form.afterCategory,
    reportingYearConfig?.year,
    effectiveStandardId,
  ].join("|");
  const selectedBeforeActivity = beforeActivities.find(
    (activity) => String(activity.activityId) === String(form.beforeActivity),
  );
  const filteredAfterActivities = useMemo(() => {
    if (scenario !== "S3") return afterActivities;
    if (
      selectedBeforeActivity?.EFId === null ||
      selectedBeforeActivity?.EFId === undefined
    ) {
      return [];
    }

    return afterActivities.filter(
      (activity) =>
        String(activity.EFId) === String(selectedBeforeActivity.EFId),
    );
  }, [afterActivities, scenario, selectedBeforeActivity]);
  const selectedAfterActivity = filteredAfterActivities.find(
    (activity) => String(activity.activityId) === String(form.afterActivity),
  );

  useEffect(() => {
    if (
      !form.afterActivity ||
      loadedAfterActivitiesKey !== afterActivitiesKey
    ) {
      return;
    }

    const hasCurrent = filteredAfterActivities.some(
      (activity) => String(activity.activityId) === String(form.afterActivity),
    );
    if (!hasCurrent) {
      setForm((prev) => ({ ...prev, afterActivity: "" }));
    }
  }, [
    afterActivitiesKey,
    filteredAfterActivities,
    form.afterActivity,
    loadedAfterActivitiesKey,
  ]);
  const currentActivity = useMemo(() => {
    return createRMActivity({
      companyId: form.companyId,
      entity: form.entity,
      name: form.activityName.trim(),
      rmType: form.rmType,
      rmSpecify: form.rmType === RM_ACTIVITY_TYPE_OTHERS ? form.rmSpecify : "",
      scenario,
      beforeScope: form.beforeScope,
      beforeCategory: form.beforeCategory,
      beforeActivity: form.beforeActivity,
      beforeSource: form.beforeSource,
      before: selectedBeforeActivity || null,
      afterScope: form.afterScope,
      afterCategory: form.afterCategory,
      afterActivity: form.afterActivity,
      afterSource: form.afterSource,
      after: selectedAfterActivity || null,
      replacementPct: scenario === "S3" ? null : Number(form.replacementPct),
    });
  }, [form, scenario, selectedBeforeActivity, selectedAfterActivity]);

  const beforeScopeOptions = Object.keys(beforeDropdown || {});
  const afterScopeOptions = Object.keys(afterDropdown || {}).filter(
    (scope) => scenario !== "S1" || String(scope) !== String(form.beforeScope),
  );
  const beforeCategoryOptions = beforeDropdown?.[form.beforeScope] || [];
  const afterCategoryOptions = afterDropdown?.[form.afterScope] || [];
  const beforeActivitiesKey = [
    form.companyId,
    form.beforeCategory,
    baseYearConfig?.year,
    effectiveStandardId,
  ].join("|");
  const showBeforeCategoryNoActivity =
    !!form.beforeCategory &&
    !beforeActivitiesLoading &&
    loadedBeforeActivitiesKey === beforeActivitiesKey &&
    beforeActivities.length === 0;
  const showBeforeNoActivity =
    !boundaryDropdownLoading &&
    !!baseYearConfig &&
    !hasBaseYearAssessmentMismatch &&
    !hasDropdownActivity(beforeDropdown);
  const hasDifferentAfterScope = afterScopeOptions.some(
    (scope) => (afterDropdown?.[scope] || []).length > 0,
  );
  const showNoDifferentAfterScope =
    scenario === "S1" &&
    !!form.beforeScope &&
    !boundaryDropdownLoading &&
    !hasDifferentAfterScope;
  const showAfterNoActivity =
    !boundaryDropdownLoading &&
    !!reportingYearConfig &&
    !hasDropdownActivity(afterDropdown);
  const showNoMatchingAfterActivity =
    scenario === "S3" &&
    !!selectedBeforeActivity &&
    !!form.afterCategory &&
    !afterActivitiesLoading &&
    loadedAfterActivitiesKey === afterActivitiesKey &&
    filteredAfterActivities.length === 0;
  const saveBlocked =
    submitting ||
    showBeforeNoActivity ||
    showAfterNoActivity ||
    showNoMatchingAfterActivity;
  const displayBaseYear =
    baseYearConfig?.year || standardContext.baseYear || editInfo?.baseYear || "-";
  const displayReportingYear =
    reportingYearConfig?.year || modalReportingYear || "-";
  const modalTitle = isEditMode
    ? "Edit Emission Removal & Mitigation Activity"
    : "Add Emission Removal & Mitigation Activity";

  const closeModal = () => {
    store._handleSetCloseEmissionActivityModal();
  };

  const submitActivity = async () => {
    if (saveBlocked) return;

    const nextErrors = {};
    if (!form.companyId) nextErrors.companyId = t("Required");
    if (!form.activityName.trim()) nextErrors.activityName = t("Required");
    if (!form.rmType) nextErrors.rmType = t("Required");
    if (!form.beforeScope) nextErrors.beforeScope = t("Required");
    if (!form.beforeCategory) nextErrors.beforeCategory = t("Required");
    if (!selectedBeforeActivity) {
      nextErrors.beforeActivity = t(BEFORE_ACTIVITY_REQUIRED_MESSAGE);
    }
    if (!form.afterScope) nextErrors.afterScope = t("Required");
    if (!form.afterCategory) nextErrors.afterCategory = t("Required");
    if (!form.afterActivity) nextErrors.afterActivity = t("Required");
    if (scenario === "S1") {
      if (showNoDifferentAfterScope) {
        nextErrors.afterScope = t(NO_DIFFERENT_AFTER_SCOPE_MESSAGE);
      } else if (
        form.beforeScope &&
        form.afterScope &&
        String(form.beforeScope) === String(form.afterScope)
      ) {
        nextErrors.afterScope = t(DIFFERENT_SCOPE_REQUIRED_MESSAGE);
      }
    }
    if (form.rmType === RM_ACTIVITY_TYPE_OTHERS && !form.rmSpecify.trim()) {
      nextErrors.rmSpecify = t("Required");
    }

    setValidationErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    if (effectiveStandardId === null || submitting) return;

    const requestBody = {
      companyId: form.companyId,
      itemName: form.activityName.trim(),
      otherName:
        form.rmType === RM_ACTIVITY_TYPE_OTHERS ? form.rmSpecify.trim() : "",
      replacement: scenario === "S3" ? 0 : Number(form.replacementPct) || 0,
      activityNameId: Number(form.rmType) || 0,
      isCrossCategory: form.q1 === "yes",
      isSourceChanged: form.q2 === "yes",
      baseYear: Number(baseYearConfig?.year || editInfo?.baseYear || 0),
      reportingYear: Number(
        reportingYearConfig?.year || editInfo?.reportingYear || 0,
      ),
      beforeActivityId:
        selectedBeforeActivity?.activityId || form.beforeActivity || "",
      beforeCategory: form.beforeCategory || "",
      afterActivityId:
        selectedAfterActivity?.activityId || form.afterActivity || "",
      afterCategory: form.afterCategory || "",
      standardId: effectiveStandardId,
    };

    setSubmitting(true);
    try {
      let response;
      if (isEditMode) {
        response = await emissionActivityModalAPI.updateRemovalMitigation(
          editActivity.id,
          requestBody,
        );
      } else {
        response =
          await emissionActivityModalAPI.createRemovalMitigation(requestBody);
      }

      if (response?.success === false) {
        handleAPIError(null, { apiData: response, throwError: false });
        setSubmitting(false);
        return;
      }

      showSuccessToast({
        apiData: response,
        scenarioCode: MESSAGE_CODES.GHG_PERFORMANCE_SAVE_SUCCESS,
      });
      await store.getRMActivityListData({ keepPage: true });
      closeModal();
    } catch (err) {
      console.error(
        `${isEditMode ? "Update" : "Create"} removal mitigation failed:`,
        err,
      );
      handleAPIError(err, { throwError: false });
      setSubmitting(false);
    }
  };

  if (!standardCheckReady) {
    return (
      <RMStandardGate
        context={standardContext}
        modalTitle={modalTitle}
        onClose={closeModal}
      />
    );
  }

  return (
    <div className={styles.modalBackdrop}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>{t(modalTitle)}</h2>
          <div className={styles.modalActions}>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={submitActivity}
              disabled={saveBlocked}
            >
              {t("Save Activity")}
            </button>
            <button
              type="button"
              className={styles.closeButton}
              onClick={closeModal}
              aria-label={t("Close")}
            >
              <CloseIcon />
            </button>
          </div>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.formGrid}>
            <Field
              label={t("Reporting Entity")}
              required
              error={validationErrors.companyId}
            >
              <SimpleSelect
                value={form.companyId}
                options={entityOptions}
                onChange={(value) => {
                  const selected = entityOptions.find(
                    (item) => item.value === value,
                  );
                  updateForm({
                    companyId: selected?.value || "",
                    entity: selected?.label || "",
                    beforeScope: "",
                    beforeCategory: "",
                    beforeActivity: "",
                    afterScope: "",
                    afterCategory: "",
                    afterActivity: "",
                  });
                }}
              />
            </Field>

            <Field
              label={t("Activity Name")}
              required
              error={validationErrors.activityName}
            >
              <input
                value={form.activityName}
                onChange={(e) => updateForm({ activityName: e.target.value })}
              />
            </Field>

            <Field
              label={t("R&M Activity")}
              required
              error={validationErrors.rmType}
            >
              <SimpleSelect
                value={form.rmType}
                options={rmActivityTypeOptions}
                onChange={(value) => updateForm({ rmType: value })}
              />
            </Field>

            {form.rmType === RM_ACTIVITY_TYPE_OTHERS && (
              <Field
                label={t("Please Specify")}
                required
                error={validationErrors.rmSpecify}
              >
                <input
                  value={form.rmSpecify}
                  onChange={(e) => updateForm({ rmSpecify: e.target.value })}
                />
              </Field>
            )}
          </div>

          <div className={styles.questionPanel}>
            <div className={styles.questionRow}>
              <div>
                <strong>{t("Q1: R&M is cross category?")}</strong>
                <div className={styles.hint}>
                  {t(
                    "Yes requires Before and After to use different scopes and categories.",
                  )}
                </div>
              </div>
              <div className={styles.toggleGroup}>
                {["yes", "no"].map((value) => (
                  <button
                    key={value}
                    type="button"
                    className={`${styles.smallButton} ${
                      form.q1 === value ? styles.activeButton : ""
                    }`}
                    onClick={() => updateForm({ q1: value })}
                  >
                    {t(value === "yes" ? "Yes" : "No")}
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.questionRow}>
              <div>
                <strong>
                  {t("Q2: Emission source / material is changed?")}
                </strong>
                <div className={styles.hint}>
                  {t(
                    "Yes means same category but new source. No means pure reduction.",
                  )}
                </div>
              </div>
              <div className={styles.toggleGroup}>
                {form.q1 === "yes" ? (
                  <span
                    className={`${styles.scenarioChip} ${styles.scenarioS1}`}
                  >
                    {t("Fixed: Yes")}
                  </span>
                ) : (
                  ["yes", "no"].map((value) => (
                    <button
                      key={value}
                      type="button"
                      className={`${styles.smallButton} ${
                        form.q2 === value ? styles.activeButton : ""
                      }`}
                      onClick={() => updateForm({ q2: value })}
                    >
                      {t(value === "yes" ? "Yes" : "No")}
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className={styles.scenarioLine}>
            <span>{t("Scenario")}</span>
            <span
              className={`${styles.scenarioChip} ${styles[scenarioMeta.className]}`}
            >
              {t(scenarioMeta.label)}
            </span>
          </div>
          {scenario === "S3" && (
            <p className={styles.scenarioHint}>
              {t(
                "Q2 = No means a pure reduction on the same source, so the After activity must use the same emission factor as Before. Only matching activities are listed.",
              )}
            </p>
          )}

          <div className={styles.compareGrid}>
            <div className={styles.compareCard}>
              <div className={styles.compareHeader}>
                <span>{t("Before")}</span>
                <span className={styles.compareYear}>
                  {t("Base Year")} {displayBaseYear}
                </span>
              </div>
              <div className={styles.panelBody}>
                {hasBaseYearAssessmentMismatch && (
                  <div className={styles.assessmentWarning}>
                    {t("Standard setting is inconsistent with Reporting Year.")}
                  </div>
                )}
                {showBeforeNoActivity && (
                  <RMNoActivityState
                    title={t(
                      "No emission activity data in Base Year {{year}} for this entity.",
                      { year: displayBaseYear },
                    )}
                    description={t(
                      "Add activity data for that year before creating an R&M activity.",
                    )}
                  />
                )}
                {!hasBaseYearAssessmentMismatch && !showBeforeNoActivity && (
                  <>
                    <div className={styles.formGrid}>
                      <Field
                        label={t("Scope")}
                        required
                        error={validationErrors.beforeScope}
                      >
                        <SimpleSelect
                          value={form.beforeScope}
                          options={beforeScopeOptions}
                          onChange={(value) =>
                            updateForm({
                              beforeScope: value,
                              beforeCategory: "",
                              beforeActivity: "",
                            })
                          }
                          disabled={
                            !form.companyId || boundaryDropdownLoading
                          }
                          placeholder={
                            !form.companyId
                              ? t(SELECT_ENTITY_FIRST_MESSAGE)
                              : undefined
                          }
                        />
                      </Field>
                      <Field
                        label={t("Category")}
                        required
                        error={validationErrors.beforeCategory}
                      >
                        <SimpleSelect
                          value={form.beforeCategory}
                          options={beforeCategoryOptions.map((category) => ({
                            value: category.code,
                            label: category.label,
                          }))}
                          onChange={(value) =>
                            updateForm({
                              beforeCategory: value,
                              beforeActivity: "",
                            })
                          }
                          disabled={
                            !form.companyId ||
                            !form.beforeScope ||
                            boundaryDropdownLoading
                          }
                          placeholder={
                            !form.companyId
                              ? t(SELECT_ENTITY_FIRST_MESSAGE)
                              : undefined
                          }
                        />
                      </Field>
                    </div>

                    <Field
                      label={t("Emission Activity Name")}
                      required
                      error={validationErrors.beforeActivity}
                    >
                      {showBeforeCategoryNoActivity && (
                        <div className={styles.assessmentWarning}>
                          {t(BEFORE_ACTIVITY_REQUIRED_MESSAGE)}
                        </div>
                      )}
                      <ActivityNameDropdown
                        value={form.beforeActivity}
                        options={beforeActivities}
                        yearType="base"
                        year={displayBaseYear}
                        disabled={
                          !form.companyId ||
                          !form.beforeCategory ||
                          beforeActivitiesLoading
                        }
                        placeholder={
                          !form.companyId
                            ? t(SELECT_ENTITY_FIRST_MESSAGE)
                            : undefined
                        }
                        onChange={(beforeActivity) =>
                          updateForm({ beforeActivity })
                        }
                      />
                    </Field>

                    <ActivityReadout activity={currentActivity.before} />

                    {scenario !== "S3" && (
                      <div
                        className={styles.formGrid}
                        style={{ marginTop: 10 }}
                      >
                        <Field label={t("Replacement %")}>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={form.replacementPct}
                            onChange={(e) =>
                              updateForm({ replacementPct: e.target.value })
                            }
                          />
                        </Field>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className={styles.compareCard}>
              <div className={styles.compareHeader}>
                <span>{t("After")}</span>
                <span className={styles.compareYear}>
                  {t("Reporting Year")} {displayReportingYear}
                </span>
              </div>
              <div className={styles.panelBody}>
                {showNoDifferentAfterScope ? (
                  <div className={styles.noActivityState}>
                    {t(NO_DIFFERENT_AFTER_SCOPE_MESSAGE)}
                  </div>
                ) : showAfterNoActivity ? (
                  <RMNoActivityState
                    title={t(
                      "No emission activity data in Reporting Year {{year}} for this entity.",
                      { year: displayReportingYear },
                    )}
                    description={t(
                      "Add activity data for that year before creating an R&M activity.",
                    )}
                  />
                ) : (
                  <>
                    <div className={styles.formGrid}>
                      <Field
                        label={t("Scope")}
                        required
                        error={validationErrors.afterScope}
                      >
                        <SimpleSelect
                          value={form.afterScope}
                          options={afterScopeOptions}
                          disabled={
                            !form.companyId ||
                            scenario !== "S1" ||
                            boundaryDropdownLoading
                          }
                          placeholder={
                            !form.companyId
                              ? t(SELECT_ENTITY_FIRST_MESSAGE)
                              : undefined
                          }
                          displayValue={
                            form.companyId && scenario !== "S1"
                              ? t(SAME_AS_BEFORE_MESSAGE)
                              : undefined
                          }
                          className={
                            scenario !== "S1" ? styles.locked : undefined
                          }
                          onChange={(value) =>
                            updateForm({
                              afterScope: value,
                              afterCategory: "",
                              afterActivity: "",
                            })
                          }
                        />
                      </Field>
                      <Field
                        label={t("Category")}
                        required
                        error={validationErrors.afterCategory}
                      >
                        <SimpleSelect
                          value={form.afterCategory}
                          options={afterCategoryOptions.map((category) => ({
                            value: category.code,
                            label: category.label,
                          }))}
                          disabled={
                            !form.companyId ||
                            !form.afterScope ||
                            scenario !== "S1" ||
                            boundaryDropdownLoading
                          }
                          placeholder={
                            !form.companyId
                              ? t(SELECT_ENTITY_FIRST_MESSAGE)
                              : undefined
                          }
                          displayValue={
                            form.companyId && scenario !== "S1"
                              ? t(SAME_AS_BEFORE_MESSAGE)
                              : undefined
                          }
                          className={
                            scenario !== "S1" ? styles.locked : undefined
                          }
                          onChange={(value) =>
                            updateForm({
                              afterCategory: value,
                              afterActivity: "",
                            })
                          }
                        />
                      </Field>
                    </div>

                    <Field
                      label={t("Emission Activity Name")}
                      required
                      error={validationErrors.afterActivity}
                    >
                      {showNoMatchingAfterActivity && (
                        <div className={styles.assessmentWarning}>
                          {t(NO_MATCHING_EF_MESSAGE)}
                        </div>
                      )}
                      <ActivityNameDropdown
                        value={form.afterActivity}
                        options={filteredAfterActivities}
                        yearType="report"
                        year={displayReportingYear}
                        disabled={
                          !form.companyId ||
                          !form.afterCategory ||
                          afterActivitiesLoading ||
                          (scenario === "S3" && !selectedBeforeActivity) ||
                          showNoMatchingAfterActivity
                        }
                        placeholder={
                          !form.companyId
                            ? t(SELECT_ENTITY_FIRST_MESSAGE)
                            : showNoMatchingAfterActivity
                              ? t(NO_MATCHING_EF_MESSAGE)
                              : undefined
                        }
                        emptyMessage={
                          scenario === "S3"
                            ? NO_MATCHING_EF_MESSAGE
                            : "No data"
                        }
                        onChange={(afterActivity) =>
                          updateForm({ afterActivity })
                        }
                      />
                    </Field>

                    <ActivityReadout activity={currentActivity.after} />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export const EmissionRemovalMitigation = observer(() => {
  const store = emissionViewStore;
  const activities = store.rmActivityListData.fullList;
  const authComKey = store.authComArr.join(",");

  useEffect(() => {
    if (!store.selectedYear || store.authComArr.length === 0) return;
    store.getRMActivityListData({ keepPage: true });
  }, [
    store.selectedView,
    authComKey,
    store.selectedYear,
    store.selectedEmissionGwpId,
    store.isRefresh,
  ]);

  if (store.selectedView === "Emission Reduction") {
    return <RMReductionView activities={activities} />;
  }

  if (store.selectedView === "Summary") {
    return <RMSummaryView activities={activities} />;
  }

  return (
    <div className={styles.container}>
      <RMActivityTable activities={store.RMCurrentPageList} />
    </div>
  );
});
